import { FC } from 'react';
import {
  Delete,
  Edit,
  MailOutlined,
  FileDownload,
  CalendarMonthOutlined,
  StickyNote2Outlined,
} from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  LpInvestorUpdateStatus,
  LpInvestorUpdateType,
} from 'further-types/lp/investor-update';
import { SortOrder } from 'further-types/utils';
import { RolePermission } from 'further-types/admin-user-role';
import { CalendarDay } from 'further-ui/utils';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import Menu from 'further-ui/components/Menu';
import { Column } from 'further-ui/components/Layout';
import DatePicker from 'further-ui/components/DatePicker';
import SearchField from 'further-ui/components/SearchField';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { useDisclosure, usePaginatedFilters } from 'further-ui/hooks';
import { lpInvestorUpdateTypeLabels } from 'further-ui/labels';
import Page from 'components/new/Page';
import PageFilters, { Filter } from 'components/new/PageFilters';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import api from 'lib/trpcClient';

const pageId = 'lp-investor-updates';
const orderByOptions = ['publishedAt'] as const;

type InvestorUpdate = {
  id: string;
  title: string;
  status: LpInvestorUpdateStatus;
  type: LpInvestorUpdateType;
  publishedAt?: CalendarDay;
  investorPdfUploads: Array<{
    investorId: string;
    path: string;
    previewUrl: string;
  }>;
};

const InvestorUpdates: FC = () => {
  const { selectedVehicleId } = useLayoutContext();
  const theme = useTheme();
  const deleteDisclosure = useDisclosure<{ id: string }>();

  const [canCreate, canEdit, canDelete] = useHasPermissions([
    RolePermission.LpCreateInvestorUpdate,
    RolePermission.LpEditInvestorUpdate,
    RolePermission.LpDeleteInvestorUpdate,
  ]);

  const { filters, handleChangeFilters, pagination, isFiltersChangePending } =
    usePaginatedFilters<
      { search: string; publishedAt: CalendarDay | undefined },
      (typeof orderByOptions)[number]
    >(pageId, {
      defaultFilters: {
        search: '',
        publishedAt: undefined,
      },
      defaultOrder: {
        orderBy: 'publishedAt',
        order: SortOrder.Desc,
      },
      orderByOptions,
    });

  const [{ results, totalCount }] = api.lpInvestorUpdate.list.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
    page: pagination.properties.page,
    rowsPerPage: pagination.properties.rowsPerPage,
    order: pagination.properties.order,
    orderBy: pagination.properties.orderBy,
    search: filters.search || undefined,
    publishedAt: filters.publishedAt,
  });

  const columns: Array<TableColumn<InvestorUpdate>> = [
    {
      label: 'Report name',
      key: 'title',
      render: ({ title }) => <Typography variant="h6">{title}</Typography>,
    },
    {
      label: 'Publication date',
      key: 'publishedAt',
      render: ({ publishedAt }) => (
        <Typography variant="h6" color={publishedAt ? 'grey.900' : 'grey.500'}>
          {publishedAt?.toReadable() ?? 'N/A'}
        </Typography>
      ),
    },
    {
      label: 'Status',
      key: 'status',
      render: ({ status }) => {
        const isPublished = status === LpInvestorUpdateStatus.Published;
        return (
          <ColorIndicatorWithText
            color={
              isPublished
                ? theme.palette.success.light
                : theme.palette.warning.light
            }
            text={isPublished ? 'Published' : 'Draft'}
            spacing="xxs"
            rounded
          />
        );
      },
    },
    {
      label: 'Update type',
      key: 'type',
      render: ({ type }) => lpInvestorUpdateTypeLabels[type],
    },
  ];

  return (
    <Page
      tabTitle="Investor Updates"
      spacing="lg"
      heading="Investor updates"
      headingIcon={MailOutlined}
      withHeadingDivider
    >
      <Column spacing="sm">
        <PageFilters
          customActions={
            canCreate ? (
              <Menu
                label="Create new update"
                buttonVariant="contained"
                alignWithIconRightEdge
                transparentBackdrop={false}
                gap="xs"
                items={[
                  {
                    label:
                      lpInvestorUpdateTypeLabels[LpInvestorUpdateType.Bulletin],
                    icon: StickyNote2Outlined,
                    onClick: () => {
                      // TODO: Will be implemented in a future PR
                      alert('bulletin update');
                    },
                  },
                  {
                    label:
                      lpInvestorUpdateTypeLabels[
                        LpInvestorUpdateType.PeriodEndReport
                      ],
                    icon: CalendarMonthOutlined,
                    onClick: () => {
                      // TODO: Will be implemented in a future PR
                      alert('period-end report');
                    },
                  },
                ]}
              />
            ) : undefined
          }
        >
          <Filter size="sm">
            <SearchField
              autoSearchOnDebounce
              name="search"
              placeholder="Search by report name"
              fullWidth
              value={filters.search}
              onSearch={(search) => {
                handleChangeFilters({ search });
              }}
              allowEmptyQuery
              iconPlacement="right"
            />
          </Filter>
          <Filter size="sm">
            <DatePicker
              value={filters.publishedAt}
              onChange={(value) => {
                handleChangeFilters({ publishedAt: value ?? undefined });
              }}
              placeholder="Select date"
              clearable
            />
          </Filter>
        </PageFilters>
        <Table
          columns={columns}
          pagination={pagination}
          isLoading={isFiltersChangePending || pagination.isPaginationChanging}
          rows={results}
          rowsTotalCount={totalCount}
          emptyMessage="No investor updates found."
          disableSorting
          actions={[
            {
              label: 'Edit',
              icon: Edit,
              hidden: !canEdit,
              onClick: () => {
                // TODO: Will be implemented in a future PR
                alert('edit');
              },
            },
            {
              label: 'Download reports',
              icon: FileDownload,
              onClick: () => {
                // TODO: Will be implemented in a future PR
                alert('download reports');
              },
            },
            {
              label: 'Delete',
              icon: Delete,
              hidden: !canDelete,
              onClick: () => {
                // TODO: Will be implemented in a future PR
                alert('delete');
              },
            },
          ]}
        />
      </Column>
      <ConfirmDialog
        maxWidth="sm"
        onClose={deleteDisclosure.onClose}
        open={deleteDisclosure.isOpen}
        title="Delete this update?"
        content="This action cannot be undone and all information relating to this update will be lost."
        actions={{
          confirm: {
            label: 'Delete',
            onClick: () => {
              alert('delete');
            },
          },
          cancel: {
            label: 'Go back',
            onClick: deleteDisclosure.onClose,
          },
        }}
      />
    </Page>
  );
};

export default InvestorUpdates;
